import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import ConfirmDialog from "src/grading/components/ConfirmDialog/ConfirmDialog";
import InlineAlert from "src/grading/components/InlineAlert/InlineAlert";
import { PrivacyImpact, PrivacyImpactChange } from "src/models/hierarchy";

export interface ImpactDialogProps {
  open: boolean;
  /** The data-removing changes being confirmed. */
  changes: PrivacyImpactChange[];
  /** Counts from `GET …/privacy-settings/impact`; undefined until they load. */
  counts?: PrivacyImpact;
  loading?: boolean;
  /** Shown when the counts could not be read; confirming stays disabled. */
  error?: string | null;
  /** The server refused the save with IMPACT_CHANGED: these are new numbers. */
  changed?: boolean;
  /** The save is running (after an IMPACT_CHANGED re-confirmation). */
  saving?: boolean;
  onRetry?: () => void;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Confirms a privacy change that removes data (FR-004, FR-006): how many students' birth dates
 * become ages, how many guardian contacts and letters lose their recipients, and that it cannot be
 * undone, behind an acknowledgement checkbox.
 */
export const ImpactDialog = ({
  open,
  changes,
  counts,
  loading = false,
  error,
  changed = false,
  saving = false,
  onRetry,
  onConfirm,
  onClose,
}: ImpactDialogProps) => {
  const { t } = useTranslation();
  const dob = changes.includes("shareDob:false");
  const print = changes.includes("letterDelivery:print");
  const only = dob && print ? "both" : dob ? "dob" : "print";

  const lines: { id: string; text: string }[] = [];
  if (counts && dob)
    lines.push({
      id: "impact-students",
      text: t("privacy.impact.students", { count: counts.students ?? 0 }),
    });
  if (counts && print) {
    lines.push({
      id: "impact-contacts",
      text: t("privacy.impact.contacts", { count: counts.contacts ?? 0 }),
    });
    lines.push({
      id: "impact-letters",
      text: t("privacy.impact.letters", { count: counts.letters ?? 0 }),
    });
  }

  const body = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {loading && !counts ? (
        <Box
          role="status"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <CircularProgress size={16} aria-hidden />
          <span>{t("privacy.impact.counting")}</span>
        </Box>
      ) : null}
      {lines.length > 0 ? (
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {lines.map((line) => (
            <Typography
              component="li"
              key={line.id}
              data-testid={line.id}
              sx={{ fontWeight: 700, fontSize: 15 }}
            >
              {line.text}
            </Typography>
          ))}
        </Box>
      ) : null}
      <Typography sx={{ fontWeight: 800 }}>
        {t("privacy.impact.cannotUndo")}
      </Typography>
      {!changed ? (
        <Typography sx={{ fontSize: 14 }}>
          {t("privacy.impact.untilSave")}
        </Typography>
      ) : null}
    </Box>
  );

  return (
    <ConfirmDialog
      open={open}
      tone="destructive"
      eyebrow={t("privacy.impact.eyebrow")}
      title={t(`privacy.impact.title.${only}`)}
      body={body}
      acknowledgement={t("privacy.impact.acknowledge")}
      cancelLabel={
        only === "dob"
          ? t("privacy.impact.keepDob")
          : only === "print"
            ? t("privacy.impact.keepEmail")
            : undefined
      }
      confirmLabel={
        changed ? t("privacy.impact.confirmSave") : t("privacy.impact.confirm")
      }
      confirmDisabled={!counts || !!error}
      loading={saving}
      testIdPrefix="impact"
      onConfirm={onConfirm}
      onClose={onClose}
    >
      {changed ? (
        <InlineAlert severity="warning">
          {t("errors.IMPACT_CHANGED")}
        </InlineAlert>
      ) : null}
      {error ? (
        <InlineAlert
          severity="error"
          action={
            onRetry ? (
              <Button color="inherit" size="small" onClick={onRetry}>
                {t("common.retry")}
              </Button>
            ) : undefined
          }
        >
          {error}
        </InlineAlert>
      ) : null}
    </ConfirmDialog>
  );
};

export default ImpactDialog;
